import { AnyStepData, InformationStepHandle, StepHandle, Variant, WorkflowExperience } from '@spiffcommerce/core'
import cn from 'classnames'
import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useShopifyMetadata } from '../../../hooks/useShopifyMetadata'
import { Button } from '../../Button/Button'
import { HandleDrawer } from '../../HandleDrawer/HandleDrawer'

export const Step = ({
  step,
  isPendantScene,
  allWorkflows,
  isFirstLoad,
  setIsFirstLoad,
  handleLengthChange,
  handlePendantQuantityChange,
  bundleInit,
  setIsLoading,
  setShowClearSelectionsPopup,
  setSceneMenuIsOpen,
  setClearSelectionLinkPath
}: {
  step: StepHandle<AnyStepData>
  isPendantScene: boolean
  allWorkflows: WorkflowExperience[]
  isFirstLoad: boolean
  setIsFirstLoad: Dispatch<SetStateAction<boolean>>
  handleLengthChange: () => Promise<void>
  handlePendantQuantityChange: (quantity: number) => Promise<void>
  bundleInit: () => void
  setIsLoading: Dispatch<SetStateAction<boolean>>
  setShowClearSelectionsPopup: Dispatch<SetStateAction<boolean>>
  setSceneMenuIsOpen: Dispatch<SetStateAction<boolean>>
  setClearSelectionLinkPath: Dispatch<SetStateAction<string>>
}) => {
  if (!step) return <></>

  const shopifyMetadata = useShopifyMetadata()
  const stepType = step.getType()
  const [currentVariant, setCurrentVariant] = useState<Variant>(step.getCurrentVariant() as Variant)
  const isBaseProductChangeStep = step.getTags().includes('base-product-change')
  const isLengthChangeStep = step.getTags().includes('length')
  const isPendantQuantityStep = step.getTags().includes('pendants')
  const isBirthstoneStep = step.getTags().includes('birthstone-step')
  const isSizeStep = step.getTags().includes('size')
  const isDiamondStep = step.getTags().includes('summary-name:diamond')
  const isLetterStep = step.getTags().includes('summary-name:letter')
  const isCharmStep = step.getTags().includes('summary-name:charm')
  const variants = step.getAvailableVariants()
  const variantsLength = variants.length
  const numberOfSelectedPendants = allWorkflows.length - 1 // -1 to account for base workflow TODO: ask Spiff on how to identify workflows by name/tag/some data etc
  const allSelectedVariants = allWorkflows.flatMap((workflow) => workflow.getSteps().flatMap((step) => step.getCurrentVariant())) as Variant[]
  const numberOfBlankLetterPositions = allSelectedVariants.filter((variant) => variant?.getName().toLowerCase().includes('blank'))
  // @ts-expect-error - Spiff types are missing the metadata property
  const metadataVariantName = currentVariant?.variantData?.thumbnail?.metadata.find((metadata) => metadata.key === 'variant_name')?.value

  const onVariantChanged = async (selectedVariantId: string) => {
    const variant = variants.find((variant: Variant) => variant.getId() === selectedVariantId)

    if (!variant) return
    setCurrentVariant(variant)
    await step.selectVariant(variant)
    step.executeAnimations()
  }

  useEffect(() => {
    setCurrentVariant(step.getCurrentVariant() as Variant)
    step.executeAnimations()
  }, [step])

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false)
    }
  }, [])

  if (!variants.length) return <></>

  return (
    <>
      <div>
        {isPendantScene && (
          <h3 className="p-0 min-h-0 Button m-0 mb-4">
            <div>
              {isBirthstoneStep && <span>Step 2: Choose Stone</span>}
              {isSizeStep && <span>Step 2: Choose Size</span>}
              {isCharmStep && <span>Step 2: Choose Charm</span>}
              {isDiamondStep && <span>Step 3: choose Shape</span>}
              {isLetterStep && <span>sTEP 2: Choose Letter:</span>}
              {!isBirthstoneStep && !isSizeStep && !isCharmStep && !isDiamondStep && !isLetterStep && <span>{step.getName()}</span>}
            </div>
          </h3>
        )}
        <div
          className={cn('mb-10',
            variantsLength >= 6
              ? isLetterStep && 'grid grid-cols-9 lg:grid-cols-13 gap-x-2.5 gap-y-6'
              : 'flex flex-wrap mb-[30px] border-solid border-ink/10',
            isPendantScene ? 'is-pendant-scene' : 'py-[30px] border-b gap-5',
            isSizeStep ? 'lg:mb-[30px] !pb-[30px] border-b gap-5' : 'lg:mb-[45px]',
            isDiamondStep && 'grid grid-cols-2 lg:grid-cols-3 !gap-2.5 !py-0 !border-b-0 !border-transparent',
            isBirthstoneStep && 'grid !grid-cols-2 !gap-2.5 !py-0 !border-b-0 !border-transparent',
            isCharmStep && 'grid grid-cols-3 !gap-2.5 py-0 !border-b-0 !border-transparent'
          )}
        >
          {variants.map((variant: Variant, i: number) => {
            const thumbnail = variant.getThumbnail()
            const variantIsBlankLetterOption = variant.getName().includes('BLANK')

            if (isBaseProductChangeStep && variant.getId() !== currentVariant?.getId()) {
              return (
                <button
                  key={i}
                  onClick={() => {
                    setShowClearSelectionsPopup(true)
                    setSceneMenuIsOpen(true)
                    setClearSelectionLinkPath(shopifyMetadata?.baseProductChange[step.getName().toLowerCase()] || '')
                  }}
                  className="cursor-pointer border-0 min-h-0"
                >
                  <span className="flex items-center gap-1 uppercase text-sm">
                    <span>{variant.getName()}</span>
                  </span>
                </button>
              )
            }

            return (
              <Button
                key={i}
                selected={variant.getId() === currentVariant?.getId()}
                onClick={async () => {
                  if (variant.getId() === currentVariant?.getId()) return
                  setIsLoading(true)

                  await onVariantChanged(variant.getId())

                  isLengthChangeStep && (await handleLengthChange())
                  isPendantQuantityStep && (await handlePendantQuantityChange(Number(variant.getName()))) // TODO: see if we can get a value from the variant instead of the name

                  setIsLoading(false)
                  bundleInit()
                }}
                className={cn(
                  'Button text-sm-b transition-none p-0 min-h-0 flex',
                  variantsLength >= 6 ? '!min-w-full' : '!min-w-[auto]',
                  isDiamondStep || isCharmStep || isBirthstoneStep ? 'border border-solid border-ink/10 py-1.5 px-2.5 bg-transparent rounded-none' : 'border-0',
                  isCharmStep ? 'flex justify-center' : '',
                  variant.getId() === currentVariant?.getId()
                    ? isDiamondStep || isCharmStep || isBirthstoneStep
                      ? 'active [&.active]:!border-ink'
                      : 'underline underline-offset-4 bg-background-light p-0'
                    : ''
                )}
                disabled={variantIsBlankLetterOption && (numberOfSelectedPendants < 3 || numberOfBlankLetterPositions.length === numberOfSelectedPendants - 2)}
              >
                <span className={cn('flex flex-row', isPendantScene ? 'gap-2.5 items-center' : 'flex-row gap-1')}>
                  {thumbnail && isPendantScene && !isSizeStep && <img className="self-center w-5 h-5" src={thumbnail} alt={'variant image'} />}
                  {!isCharmStep && <span>{variant.getName()}</span>}
                </span>
              </Button>
            )
          })}
        </div>
      </div>
    </>
  )
}
